import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

const CONTRACTS_COLLECTION = 'contracts';
const BROKERAGE_SETTINGS_COLLECTION = 'brokerageSettings';

export const contractService = {
  // Generate unique 5-character alphanumeric ID (excluding i, l, o)
  generateContractId() {
    const chars = 'abcdefghjkmnpqrstuvwxyz0123456789'; // Excluding i, l, o
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result.toUpperCase();
  },

  // Check if contract ID already exists
  async isContractIdUnique(contractId) {
    try {
      const q = query(
        collection(db, CONTRACTS_COLLECTION),
        where('contractId', '==', contractId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.empty;
    } catch (error) {
      throw error;
    }
  },

  // Generate unique contract ID
  async generateUniqueContractId() {
    let contractId;
    let attempts = 0;
    const maxAttempts = 100;

    do {
      contractId = this.generateContractId();
      attempts++;
      
      if (attempts > maxAttempts) {
        throw new Error('Unable to generate unique contract ID after maximum attempts');
      }
    } while (!(await this.isContractIdUnique(contractId)));

    return contractId;
  },
  // Create a new contract
  async createContract(contractData) {
    try {
      // Generate unique contract ID
      const contractId = await this.generateUniqueContractId();
      
      // Calculate amounts
      const totalRentalAmount = parseFloat(contractData.totalRentalAmount);
      const numberOfCheques = parseInt(contractData.numberOfCheques);
      const contractDuration = parseInt(contractData.contractDuration) || 12; // Default to 12 months if not specified
      const monthlyRent = totalRentalAmount / contractDuration; // Monthly rent = total rent / contract duration
      const chequeAmount = totalRentalAmount / numberOfCheques; // Installment = total rent / number of cheques
      
      // Calculate brokerage fee
      const brokeragePercentage = await this.getBrokeragePercentage(contractData.companyId);
      const brokerageFee = (totalRentalAmount * brokeragePercentage) / 100;
      
      const contract = {
        ...contractData,
        contractId, // Add the unique contract ID
        totalRentalAmount,
        numberOfCheques,
        contractDuration,
        monthlyRent: parseFloat(monthlyRent.toFixed(2)), // Monthly rent
        chequeAmount: parseFloat(chequeAmount.toFixed(2)), // Installment amount
        brokeragePercentage,
        brokerageFee: parseFloat(brokerageFee.toFixed(2)),
        totalAmount: totalRentalAmount + brokerageFee,
        currency: contractData.currency || 'USD',
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, CONTRACTS_COLLECTION), contract);
      return { id: docRef.id, ...contract };
    } catch (error) {
      throw error;
    }
  },

  // Get all contracts for a company
  async getContractsByCompany(companyId) {
    try {
      const q = query(
        collection(db, CONTRACTS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const contracts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      return contracts.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      throw error;
    }
  },

  // Get contract by ID
  async getContractById(contractId) {
    try {
      const docRef = doc(db, CONTRACTS_COLLECTION, contractId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Contract not found');
      }
    } catch (error) {
      throw error;
    }
  },

  // Update contract
  async updateContract(contractId, updateData) {
    try {
      // Recalculate amounts if rental amount or number of cheques changed
      if (updateData.totalRentalAmount || updateData.numberOfCheques) {
        const existingContract = await this.getContractById(contractId);
        const totalRentalAmount = parseFloat(updateData.totalRentalAmount || existingContract.totalRentalAmount);
        const numberOfCheques = parseInt(updateData.numberOfCheques || existingContract.numberOfCheques);
        const contractDuration = parseInt(updateData.contractDuration) || 12; // Default to 12 months if not specified
        const monthlyRent = totalRentalAmount / contractDuration; // Monthly rent = total rent / contract duration
        const chequeAmount = totalRentalAmount / numberOfCheques; // Installment = total rent / number of cheques
        
        const brokeragePercentage = await this.getBrokeragePercentage(existingContract.companyId);
        const brokerageFee = (totalRentalAmount * brokeragePercentage) / 100;
        
        updateData = {
          ...updateData,
          totalRentalAmount,
          numberOfCheques,
          contractDuration,
          monthlyRent: parseFloat(monthlyRent.toFixed(2)), // Monthly rent
          chequeAmount: parseFloat(chequeAmount.toFixed(2)), // Installment amount
          brokeragePercentage,
          brokerageFee: parseFloat(brokerageFee.toFixed(2)),
          totalAmount: totalRentalAmount + brokerageFee
        };
      }

      const docRef = doc(db, CONTRACTS_COLLECTION, contractId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
      return { id: contractId, ...updateData };
    } catch (error) {
      throw error;
    }
  },

  // Delete contract
  async deleteContract(contractId) {
    try {
      const docRef = doc(db, CONTRACTS_COLLECTION, contractId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Get contracts by tenant
  async getContractsByTenant(tenantId) {
    try {
      const q = query(
        collection(db, CONTRACTS_COLLECTION),
        where('tenantId', '==', tenantId)
      );
      const querySnapshot = await getDocs(q);
      const contracts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      return contracts.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      throw error;
    }
  },

  // Get contracts by property unit
  async getContractsByPropertyUnit(unitId) {
    try {
      const q = query(
        collection(db, CONTRACTS_COLLECTION),
        where('propertyUnitId', '==', unitId)
      );
      const querySnapshot = await getDocs(q);
      const contracts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in JavaScript instead of Firestore to avoid index requirement
      const sortedContracts = contracts.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
      return sortedContracts;
    } catch (error) {
      throw error;
    }
  },

  // ===== BROKERAGE SETTINGS =====
  
  // Get brokerage percentage for a company
  async getBrokeragePercentage(companyId) {
    try {
      const q = query(
        collection(db, BROKERAGE_SETTINGS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // Return default 5% if no settings found
        return 5;
      }
      
      const settings = querySnapshot.docs[0].data();
      return settings.brokeragePercentage || 5;
    } catch (error) {
      throw error;
    }
  },

  // Update brokerage settings
  async updateBrokerageSettings(companyId, brokeragePercentage, currency = 'USD', logoUrl = '', logoEnabled = false) {
    try {
      const q = query(
        collection(db, BROKERAGE_SETTINGS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // Create new settings
        await addDoc(collection(db, BROKERAGE_SETTINGS_COLLECTION), {
          companyId,
          brokeragePercentage,
          currency,
          logoUrl,
          logoEnabled,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      } else {
        // Update existing settings
        const docRef = doc(db, BROKERAGE_SETTINGS_COLLECTION, querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          brokeragePercentage,
          currency,
          logoUrl,
          logoEnabled,
          updatedAt: new Date()
        });
      }
      
      return { companyId, brokeragePercentage, currency, logoUrl, logoEnabled };
    } catch (error) {
      throw error;
    }
  },

  // Get brokerage settings
  async getBrokerageSettings(companyId) {
    try {
      const q = query(
        collection(db, BROKERAGE_SETTINGS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          companyId,
          brokeragePercentage: 5,
          currency: 'USD',
          logoUrl: '',
          logoEnabled: false,
          createdAt: null,
          updatedAt: null
        };
      }
      
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      throw error;
    }
  },

  // Calculate contract amounts (utility function)
  calculateContractAmounts(totalRentalAmount, numberOfCheques, brokeragePercentage) {
    const totalRental = parseFloat(totalRentalAmount);
    const cheques = parseInt(numberOfCheques);
    const brokerage = parseFloat(brokeragePercentage);
    
    const monthlyRent = totalRental / 12; // Monthly rent = total rent / 12 months
    const chequeAmount = totalRental / cheques; // Installment = total rent / number of cheques
    const brokerageFee = (totalRental * brokerage) / 100;
    const totalAmount = totalRental + brokerageFee;
    
    return {
      totalRentalAmount: totalRental,
      numberOfCheques: cheques,
      monthlyRent: parseFloat(monthlyRent.toFixed(2)), // Monthly rent
      chequeAmount: parseFloat(chequeAmount.toFixed(2)), // Installment amount
      brokeragePercentage: brokerage,
      brokerageFee: parseFloat(brokerageFee.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  },

  // Fix existing contracts by adding monthlyRent field
  async fixExistingContracts(companyId) {
    try {
      const contracts = await this.getContractsByCompany(companyId);
      let fixedCount = 0;
      
      for (const contract of contracts) {
        if (!contract.monthlyRent && contract.totalRentalAmount) {
          const contractDuration = parseInt(contract.contractDuration) || 12; // Default to 12 months if not specified
          const monthlyRent = parseFloat(contract.totalRentalAmount) / contractDuration;
          
          // Update the contract with monthlyRent
          await this.updateContract(contract.id, {
            monthlyRent: parseFloat(monthlyRent.toFixed(2))
          });
          
          fixedCount++;
        }
      }
      
      return fixedCount;
    } catch (error) {
      throw error;
    }
  },

  // Upload company logo
  async uploadCompanyLogo(companyId, file) {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `company-logos/${companyId}/logo_${timestamp}.${fileExtension}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update brokerage settings with logo URL
      const settingsRef = doc(db, BROKERAGE_SETTINGS_COLLECTION, companyId);
      await updateDoc(settingsRef, {
        logoUrl: downloadURL,
        logoFileName: fileName,
        updatedAt: new Date()
      });
      
      return downloadURL;
    } catch (error) {
      throw error;
    }
  },

  // Remove company logo
  async removeCompanyLogo(companyId) {
    try {
      // Get current settings to find logo file
      const settings = await this.getBrokerageSettings(companyId);
      
      if (settings.logoFileName) {
        // Delete from Firebase Storage
        const storageRef = ref(storage, settings.logoFileName);
        await deleteObject(storageRef);
      }
      
      // Remove logo URL from settings
      const settingsRef = doc(db, BROKERAGE_SETTINGS_COLLECTION, companyId);
      await updateDoc(settingsRef, {
        logoUrl: null,
        logoFileName: null,
        updatedAt: new Date()
      });
      
      return true;
    } catch (error) {
      throw error;
    }
  }
}; 